import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { gql, Apollo } from 'apollo-angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataService } from 'src/app/_services/data';
import { SubscriptionCartService } from 'src/app/_services/subscription-cart.service';
import { CompanyService } from 'src/app/_services/company.services';

const getCompanyLocationList = gql`
  query {
    getCompanyLocationList {
      id
      label
      country
      province
      city
      street
      postalCode
      phone
      taxId
      isMainAddress
    }
  }
`;

const submitOfferMutation = gql`
  mutation submitOffer($companyBillingLocation: ID!, $billingItemSelected:[BillingItemSelectedInput]!) {
      submitOffer(input: {
          companyBillingLocation: $companyBillingLocation
          billingItemSelected: $billingItemSelected
      })
  }
`;

// const submitOfferMutation = gql`

//   mutation submitOffer($input: SubmitOffer!) {
//       submitOffer(input: $input)
//   }

// `;
@Component({
  selector: 'app-subscription-cart',
  templateUrl: './subscription-cart.component.html',
  styleUrls: ['./subscription-cart.component.scss']
})
export class SubscriptionCartComponent implements OnInit, OnDestroy {
  currentLang: any;
  isLoading: boolean = false;
  isProcessing: boolean = false;
  loadingError: any = null;

  // User data
  userdata: any;
  usercompany: any;
  isLoggedin: boolean = false;

  // Cart data
  cartItems: any = {};
  cartItemCount: number = 0;
  subscriptions: any[] = [];
  addons: any[] = [];
  vouchers: any[] = [];

  // Company addresses
  companyAddresses: any[] = [];
  selectedAddressId: string = '';

  // Totals and tax
  subtotal: number = 0;
  // taxRate: number = 0.10; // 10% tax
  taxRate: number = 0; // 0% tax
  taxAmount: number = 0;
  totalAmount: number = 0;

  // Pro-rate state
  isSubsribeFhProduct: boolean = false;
  subscriptionInfoPrice: { [key: string]: any } = {};

  // UI State
  showNotificationMessage: boolean = false;
  notificationMessage: string = '';
  notificationType: string = 'success';

  companyName: string = '';

  private destroy$ = new Subject<void>();

  constructor(
    public translate: TranslateService,
    private router: Router,
    private apollo: Apollo,
    private dataService: DataService,
    private cartService: SubscriptionCartService,
    private companyService: CompanyService
  ) { }

  ngOnInit(): void {
    this.currentLang = localStorage.getItem('language') || 'id';
    this.loadProfile();
    this.loadCartItems();
    this.loadCompanyAddresses();
    this.loadActiveFhProduct();
    
    // Subscribe to cart changes
    this.cartService.cart$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cart: any) => {
        this.cartItems = cart;
        this.processCartItems();
        this.calculateTotals();
      });


    this.companyName = localStorage.getItem('COMPANYSELECTNAME') || ''; 
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load user profile from localStorage
   */
  loadProfile(): void {
    const getuserdata = localStorage.getItem('USERDATA');
    if (getuserdata !== null) {
      this.userdata = JSON.parse(getuserdata);
      this.isLoggedin = true;
      console.log('User data loaded:', this.userdata);
    }

    const getusercompany = localStorage.getItem('USERCOMPANY');
    if (getusercompany !== null) {
      this.usercompany = JSON.parse(getusercompany);
      console.log('User company loaded:', this.usercompany);
    }
  }

  /**
   * Load cart items from cart service
   */
  loadCartItems(): void {
    const cart = this.cartService.getCart();
    this.cartItems = cart;
    this.processCartItems();
    this.calculateTotals();
  }

  /**
   * Process cart items and organize by type
   */
  private processCartItems(): void {
    this.subscriptions = this.cartService.getItemsByType('subscription');
    this.addons = this.cartService.getItemsByType('addon');
    this.vouchers = this.cartService.getItemsByType('voucher');
    this.cartItemCount = this.cartService.getCartCount();

    console.log('Cart items processed:', {
      subscriptions: this.subscriptions,
      addons: this.addons,
      vouchers: this.vouchers,
      total: this.cartItemCount
    });

    this.loadActiveSubscriptionInfoPrice();
  }

  /**
   * Load company addresses from API
   */
  loadCompanyAddresses(): void {
    this.isLoading = true;
    this.apollo.query({
      query: getCompanyLocationList,
      fetchPolicy: 'no-cache'
    }).pipe(takeUntil(this.destroy$))
      .subscribe(
        (result: any) => {
          console.log('Company locations loaded:', result);
          if (result.data && result.data.getCompanyLocationList) {
            this.companyAddresses = result.data.getCompanyLocationList;
            
            // Set default address
            const defaultAddress = this.companyAddresses.find(addr => addr.isMainAddress);
            if (defaultAddress) {
              this.selectedAddressId = defaultAddress.id;
            } else if (this.companyAddresses.length > 0) {
              this.selectedAddressId = this.companyAddresses[0].id;
            }
          }
          this.isLoading = false;
        },
        (error: any) => {
          console.error('Error loading addresses:', error);
          this.loadingError = error;
          this.isLoading = false;
        }
      );
  }

  /**
   * Calculate totals including tax
   */
  private calculateTotals(): void {
    let calculatedSubtotal = 0;

    // Add subscription totals (with pro-rate if applicable)
    this.subscriptions.forEach((item: any) => {
      calculatedSubtotal += this.getProductPrice(item, 'subscription') * (item.quantity || 1);
    });

    // Add addon totals
    this.addons.forEach((item: any) => {
      const price = item.addOnPrice - item.addOnDiscount || 0;
      const quantity = item.quantity || 1;
      calculatedSubtotal += price * quantity;
    });

    // Add voucher totals
    this.vouchers.forEach((item: any) => {
      const price = item.voucherPrice - item.voucherDiscount || 0;
      const quantity = item.quantity || 1;
      calculatedSubtotal += price * quantity;
    });

    this.subtotal = calculatedSubtotal;
    this.taxAmount = this.subtotal * this.taxRate;
    this.totalAmount = this.subtotal + this.taxAmount;
  }

  /**
   * Get product name based on type
   */
  getProductName(item: any, type: string): string {
    switch (type) {
      case 'subscription':
        return item.subscriptionName || 'Subscription';
      case 'addon':
        return item.addOnName || 'Add-on';
      case 'voucher':
        return item.voucherName || 'Voucher';
      default:
        return 'Product';
    }
  }

  /**
   * Get product description based on type
   */
  getProductDescription(item: any, type: string): string {
    switch (type) {
      case 'addon':
        return `${item.qtyOffered || ''} kuota ${item.addOnName || ''}`.trim();
      case 'voucher':
        return `${item.creditOffered || ''} kredit ${item.voucherName || ''}`.trim();
      default:
        return '';
    }
  }

  /**
   * Get product price based on type (with pro-rate for subscriptions)
   */
  getProductPrice(item: any, type: string): number {
    switch (type) {
      case 'subscription': {
        const info = this.subscriptionInfoPrice?.[item.fhProductOfferingId];
        if (this.isSubsribeFhProduct && info?.subscriptionBuyingOption) {
          const option = info.subscriptionBuyingOption.toLowerCase();
          if (option === 'upgrade') {
            return info.priceReductionResult || 0;
          } else if (option === 'downgrade') {
            return 0;
          }
        }
        return (item.subscriptionPrice || 0) - (item.subscriptionDiscount || 0);
      }
      case 'addon':
        return item.addOnPrice - item.addOnDiscount || 0;
      case 'voucher':
        return item.voucherPrice - item.voucherDiscount || 0;
      default:
        return 0;
    }
  }

  /**
   * Format currency to IDR
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  /**
   * Remove item from cart
   */
  removeFromCart(productOfferingId: string, type: string): void {
    this.cartService.removeFromCart(productOfferingId, type);
    this.showNotification('Product removed from cart', 'success');
  }

  /**
   * Increment quantity by 1
   */
  incrementQuantity(item: any): void {
    let qty = (item.quantity || 1) + 1;
    
    // Validate max
    if (qty > 999) {
      qty = 999;
    }

    // Update the original item in the correct array
    this.updateOriginalItemQuantity(item, qty);
    this.calculateTotals();
  }

  /**
   * Decrement quantity by 1
   */
  decrementQuantity(item: any): void {
    let qty = (item.quantity || 1) - 1;
    
    // Validate min
    if (qty < 1) {
      qty = 1;
    }

    // Update the original item in the correct array
    this.updateOriginalItemQuantity(item, qty);
    this.calculateTotals();
  }

  /**
   * Update the original item quantity in its source array
   */
  private updateOriginalItemQuantity(item: any, quantity: number): void {
    if (item.type === 'subscription') {
      const originalItem = this.subscriptions.find(s => s.fhProductOfferingId === item.fhProductOfferingId);
      if (originalItem) {
        originalItem.quantity = quantity;
        this.cartService.updateQuantity(item.fhProductOfferingId, 'subscription', quantity);
      }
    } else if (item.type === 'addon') {
      const originalItem = this.addons.find(a => a.fhProductOfferingId === item.fhProductOfferingId);
      if (originalItem) {
        originalItem.quantity = quantity;
        this.cartService.updateQuantity(item.fhProductOfferingId, 'addon', quantity);
      }
    } else if (item.type === 'voucher') {
      const originalItem = this.vouchers.find(v => v.fhProductOfferingId === item.fhProductOfferingId);
      if (originalItem) {
        originalItem.quantity = quantity;
        this.cartService.updateQuantity(item.fhProductOfferingId, 'voucher', quantity);
      }
    }
    // Also update the temporary item object for immediate UI update
    item.quantity = quantity;
  }

  /**
   * Handle quantity blur event - only update on blur, not on every keystroke
   */
  onQuantityBlur(event: any, item: any): void {
    let qty = parseInt(event.target.value, 10);
    
    // Validate range
    if (isNaN(qty) || qty < 1) {
      qty = 1;
    } else if (qty > 999) {
      qty = 999;
    }

    // Update the original item in the correct array
    this.updateOriginalItemQuantity(item, qty);
    
    // Reset the input to validated value
    event.target.value = qty;

    // Update the cart total
    this.calculateTotals();
  }

  /**
   * Update item quantity
   */
  updateItemQuantity(item: any): void {
    // Ensure quantity is a valid number
    let qty = parseInt(item.quantity, 10);
    
    // Validate range
    if (isNaN(qty) || qty < 1) {
      qty = 1;
    } else if (qty > 999) {
      qty = 999;
    }

    // Update the original item in the correct array
    this.updateOriginalItemQuantity(item, qty);

    // Update the cart total
    this.calculateTotals();
  }

  /**
   * Get all cart items as flat list
   */
  getAllCartItems(): any[] {
    const allItems: any[] = [];
    
    // Add subscriptions
    this.subscriptions.forEach((item, index) => {
      allItems.push({
        ...item,
        type: 'subscription',
        index: index + 1
      });
    });

    // Add addons
    this.addons.forEach((item, index) => {
      allItems.push({
        ...item,
        type: 'addon',
        index: this.subscriptions.length + index + 1
      });
    });

    // Add vouchers
    this.vouchers.forEach((item, index) => {
      allItems.push({
        ...item,
        type: 'voucher',
        index: this.subscriptions.length + this.addons.length + index + 1
      });
    });

    return allItems;
  }

  /**
   * Get selected address details
   */
  getSelectedAddressDetails(): any {
    return this.companyAddresses.find(addr => addr.id === this.selectedAddressId);
  }

  /**
   * Format address for display
   */
  formatAddress(address: any): string {
    if (!address) return '';
    const parts = [
      address.street,
      address.city,
      address.province,
      address.postalCode,
      address.country
    ].filter(part => part);
    return parts.join(', ');
  }

  /**
   * Validate form before submission
   */
  validateBeforeSubmit(): boolean {
    const errors: string[] = [];

    if (!this.selectedAddressId) {
      errors.push('Please select a delivery address');
    }

    if (this.cartItemCount === 0) {
      errors.push('Your cart is empty');
    }

    if (errors.length > 0) {
      this.showNotification(errors.join('; '), 'error');
      return false;
    }

    return true;
  }

  /**
   * Proceed to checkout
   */
  proceedToCheckout(): void {
    if (!this.validateBeforeSubmit()) {
      return;
    }

    this.isProcessing = true;

    try {
      // Build billing items array from cart items
      const billingItemSelected = this.getAllCartItems().map(item => ({
        fhProductOfferingId: item.fhProductOfferingId,
        qtyToOrder: item.quantity || 1
      }));



      // Call GraphQL mutation
      this.apollo.mutate({
        mutation: submitOfferMutation,
        variables: {
          companyBillingLocation: this.selectedAddressId,
          billingItemSelected: billingItemSelected
        }
      }).pipe(takeUntil(this.destroy$))
        .subscribe(
          (result: any) => {
            console.log('Order submitted successfully:', result);
            this.showNotification('Order submitted successfully!', 'success');

            if(result.data.submitOffer){
              this.router.navigate(['/billing-list/detail/'+result.data.submitOffer]); // submitOffer: "01KH676MT8C637711YRR6CP2R6"
            } else{
              this.router.navigate(['/billing-list']);
            }
            // // Navigate to company management page
            // setTimeout(() => {
            //   this.router.navigate(['/subscription-list']);
            // }, 1000);
          },
          (error: any) => {
            this.isProcessing = false;
            this.showNotification(
              'Error submitting order: ' + (error.message || 'Unknown error'),
              'error'
            );
            console.error('Error submitting order:', error);
          }
        );
    } catch (error: any) {
      this.isProcessing = false;
      this.showNotification('Error processing order: ' + (error.message || 'Unknown error'), 'error');
      console.error('Error processing order:', error);
    }
  }

  /**
   * Go back to products page
   */
  goBackToProducts(): void {
    this.router.navigate(['/subscription-products']);
  }

  /**
   * Show notification
   */
  private showNotification(message: string, type: 'success' | 'error' | 'warning' = 'success'): void {
    this.notificationMessage = message;
    this.notificationType = type;
    this.showNotificationMessage = true;

    // Auto-hide notification after 4 seconds
    setTimeout(() => {
      this.showNotificationMessage = false;
    }, 4000);
  }

  /**
   * Switch language
   */
  switchLang(lang: any): void {
    this.translate.use(lang);
    localStorage.setItem('language', lang);
    this.currentLang = lang;
  }

  /**
   * Load active FH product to check if user has active subscription
   */
  private loadActiveFhProduct(): void {
    this.companyService.getgetActiveFhProduct().subscribe(
      (result: any) => {
        if (result.data && result.data.getActiveFhProduct) {
          const activeFhProducts = result.data.getActiveFhProduct;
          this.isSubsribeFhProduct = Array.isArray(activeFhProducts) && activeFhProducts.length > 0;
        } else {
          this.isSubsribeFhProduct = false;
        }
        this.calculateTotals();
      },
      () => { this.isSubsribeFhProduct = false; }
    );
  }

  /**
   * Load active subscription info price for each subscription in cart
   */
  private loadActiveSubscriptionInfoPrice(): void {
    if (this.subscriptions.length === 0) return;

    this.subscriptions.forEach((item: any) => {
      const fhProductOfferingId = item.fhProductOfferingId;
      this.companyService.getgetActiveSubscriptionInfoPrice({ fhProductOfferingId }).subscribe(
        (result: any) => {
          if (result.data && result.data.getActiveSubscriptionInfoPrice) {
            this.subscriptionInfoPrice[fhProductOfferingId] = result.data.getActiveSubscriptionInfoPrice;
            this.calculateTotals();
          }
        },
        (error: any) => {
          console.error(`[loadActiveSubscriptionInfoPrice] Error for ${fhProductOfferingId}:`, error);
        }
      );
    });
  }
}